import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

/**
 * ResponsiveLayout Component
 * Provides a mobile-friendly layout with collapsible sidebar
 */
export const ResponsiveLayout = ({ 
  children, 
  sidebar, 
  header,
  user,
  onLogout 
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 h-16">
        <div className="flex items-center justify-between h-full px-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          {header && <div className="flex-1 text-center">{header}</div>}
          
          {user && (
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium text-gray-700 hidden sm:block">
                {user.name || user.email}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 mt-16"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 lg:top-0 left-0 h-[calc(100vh-4rem)] lg:h-screen
          w-64 bg-white border-r border-gray-200 z-40
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          overflow-y-auto
        `}
      >
        {sidebar}
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
};

/**
 * ResponsiveContainer Component
 * Provides consistent padding and max-width for content
 */
export const ResponsiveContainer = ({ children, className = '' }) => {
  return (
    <div className={`w-full px-4 sm:px-6 lg:px-8 py-6 ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveGrid Component
 * Responsive grid that adapts to screen size
 */
export const ResponsiveGrid = ({ children, cols = 3, className = '' }) => {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${gridCols[cols] || gridCols[3]} gap-4 ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveTable Component
 * Makes tables scrollable on mobile
 */
export const ResponsiveTable = ({ children, className = '' }) => {
  return (
    <div className="w-full overflow-x-auto -mx-4 sm:mx-0">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * ResponsiveCard Component
 * Card that adapts to mobile screens
 */
export const ResponsiveCard = ({ children, className = '', onClick }) => {
  return (
    <div
      className={`
        bg-white rounded-lg shadow-sm border border-gray-200
        p-4 sm:p-6 hover:shadow-md transition-shadow
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

/**
 * MobileActionButton Component
 * Fixed action button for mobile
 */
export const MobileActionButton = ({ onClick, icon: Icon, label, className = '' }) => {
  return (
    <>
      {/* Desktop Button */}
      <button
        onClick={onClick}
        className={`hidden sm:inline-flex items-center px-4 py-2 rounded-lg ${className}`}
      >
        {Icon && <Icon className="w-4 h-4 mr-2" />}
        {label}
      </button>

      {/* Mobile Floating Button */}
      <button
        onClick={onClick}
        className={`
          sm:hidden fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg
          flex items-center justify-center z-30
          ${className}
        `}
      >
        {Icon && <Icon className="w-6 h-6" />}
      </button>
    </>
  );
};

/**
 * ResponsiveDialog Component
 * Makes dialogs full-screen on mobile
 */
export const ResponsiveDialogContent = ({ children, title, onClose, className = '' }) => {
  return (
    <div className={`
      fixed inset-0 sm:relative sm:inset-auto
      bg-white sm:rounded-lg sm:max-w-2xl sm:w-full
      flex flex-col
      ${className}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {children}
      </div>
    </div>
  );
};

/**
 * ResponsiveFormGrid Component
 * Form grid that stacks on mobile
 */
export const ResponsiveFormGrid = ({ children, cols = 2, className = '' }) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  };

  return (
    <div className={`grid ${gridCols[cols] || gridCols[2]} gap-4 ${className}`}>
      {children}
    </div>
  );
};

/**
 * ResponsiveStats Component
 * Statistics cards that adapt to screen size
 */
export const ResponsiveStats = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 truncate">{stat.label}</span>
            {stat.icon && <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
            {stat.value}
          </div>
          {stat.change && (
            <div className={`text-xs sm:text-sm mt-1 ${
              stat.change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {stat.change > 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ResponsiveLayout;
